import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button } from '@mui/material';

import {
  Channel,
  VirtualizedMessageList,
  MessageInput,
} from 'stream-chat-react';

import ChatDateSeparator from '../ChatDateSeparator';
import ChatMessage from '../ChatMessage';
import ChatInput from '../ChatInput';
import ConfirmClearChatModal, {
  useOpenConfirmClearChatModal,
} from '../ConfirmClearChatModal';

import { IChatChannel } from './interfaces';

import { useStyles } from './styles';

const ChatChannel = ({ channel, showClearChat = true }: IChatChannel) => {
  const classes = useStyles();

  const { open, openModal, closeModal } = useOpenConfirmClearChatModal();
  const { t: translate } = useTranslation('common');

  const chatRef = useRef<HTMLElement | null>();

  useEffect(() => {
    if (!chatRef.current) {
      return;
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      if (['arrowdown', 'arrowup'].includes(event.key.toLowerCase())) {
        event.preventDefault();
      }
    };

    chatRef.current?.addEventListener('keydown', handleKeyPress);

    return () => {
      chatRef.current?.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <Box sx={classes.root} ref={chatRef}>
      <Channel channel={channel} DateSeparator={ChatDateSeparator}>
        {showClearChat && (
          <>
            <Button
              color="error"
              variant="contained"
              sx={classes.clearHistoryBtn}
              disableRipple
              onClick={openModal}
            >
              {translate('chatDrawer.clearChat')}
            </Button>
            <ConfirmClearChatModal
              open={open}
              onClose={closeModal}
              channel={channel}
            />
          </>
        )}
        <VirtualizedMessageList
          Message={() => {
            return <ChatMessage channel={channel} />;
          }}
          hideNewMessageSeparator
          hideDeletedMessages
          shouldGroupByUser
          defaultItemHeight={67}
          stickToBottomScrollBehavior="auto"
          scrollToLatestMessageOnFocus={false}
        />
        <MessageInput Input={() => <ChatInput />} maxRows={5} grow />
      </Channel>
    </Box>
  );
};

export default React.memo(ChatChannel);
